import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { Plus, Trash2 } from "lucide-react";

/**
 * v5.8 Addition 20 — Terminology overrides.
 * VC Admin can rename common CompeteWiser terms (Deal → Startup, Founder → Entrepreneur, ...).
 * Stored on tenants.terminology and consumed by anywhere in the UI that reads it.
 */
const SUGGESTED_TERMS = ["Deal", "Deals", "Founder", "Founders", "Pipeline", "Stage",
                         "Gate", "Agent", "Task", "Notification", "Pitch Deck"];

export default function TerminologyOverrides() {
  const { refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tenant/settings");
    const term = data.terminology || {};
    const initial = Object.entries(term).map(([k, v]) => ({ from: k, to: v }));
    if (initial.length === 0) initial.push({ from: "", to: "" });
    setRows(initial);
  };
  useEffect(() => { load(); }, []);

  const addRow = () => setRows([...rows, { from: "", to: "" }]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => setRows(rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const save = async () => {
    const payload = {};
    for (const r of rows) {
      if ((r.from || "").trim() && (r.to || "").trim()) payload[r.from.trim()] = r.to.trim();
    }
    setBusy(true);
    try {
      await api.patch("/tenant/terminology", { terminology: payload });
      toast.success("Terminology updated · applying across the app…");
      // Refresh AuthProvider so tenant.terminology becomes live everywhere.
      await refresh();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cw-card p-5 space-y-4" data-testid="terminology-panel">
      <div>
        <h3 className="cw-h3">Rename CompeteWiser terms</h3>
        <p className="text-xs text-[var(--cw-muted)] mt-1">
          Replace the built-in words your team sees. Example: "Deal" → "Startup".
          Changes apply across sidebar nav, page titles, buttons and toasts.
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-semibold text-[var(--cw-muted)]">
          <div className="w-48">Original</div>
          <div className="flex-1">Rename to</div>
        </div>
        {rows.map((r, idx) => (
          <div key={idx} className="flex items-center gap-3" data-testid={`term-row-${idx}`}>
            <input list="cw-term-suggestions" value={r.from} placeholder="Original term"
              onChange={(e) => updateRow(idx, { from: e.target.value })}
              className="w-48 h-9 px-3 rounded-md border text-sm bg-white"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`term-from-${idx}`} />
            <input value={r.to} placeholder="Your term"
              onChange={(e) => updateRow(idx, { to: e.target.value })}
              className="flex-1 h-9 px-3 rounded-md border text-sm bg-white"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`term-to-${idx}`} />
            <button className="p-1.5 rounded hover:bg-red-50" onClick={() => removeRow(idx)}
              data-testid={`term-remove-${idx}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <datalist id="cw-term-suggestions">
          {SUGGESTED_TERMS.map((t) => <option key={t} value={t} />)}
        </datalist>
      </div>
      <div className="flex justify-between">
        <button className="cw-btn-secondary flex items-center gap-1" onClick={addRow}
          data-testid="term-add">
          <Plus size={14} /> Add another
        </button>
        <button className="cw-btn-primary" onClick={save} disabled={busy}
          data-testid="term-save">
          {busy ? "Saving…" : "Save terminology"}
        </button>
      </div>
    </div>
  );
}
